import React, { useState, useEffect, useCallback } from 'react';
import { Select, Spin, Empty, Tag } from 'antd';
import useGetSPDXLicenses from '../../hooks/query/useGetSPDXLicenses';
import ParamFormItem from './ParamFormItem';
import { Param } from '../../cliDataSchema';

interface LicenseOptionType {
  label: string;
  value: string;
  type: string;
}

interface Props {
  selectedValue?: string;
  handleSelectChange: (value: string) => void;
  param: Param;
  visibleParams: Param[];
}

const NEW_LICENSE_TYPE = 'new-license';

const RemoteProjectLicenseSelection: React.FC<Props> = ({
  selectedValue,
  handleSelectChange,
  param,
  visibleParams,
}) => {
  const [disabled, setDisabled] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<LicenseOptionType[]>(
    [],
  );
  const { data: licenses, isLoading } = useGetSPDXLicenses();

  const formatLicenseOptions = (licenses: any[]): LicenseOptionType[] =>
    licenses.map(({ name, licenseId, additionalProp1 }) => ({
      label: `${name} (${licenseId})`,
      value: licenseId,
      type: 'existing',
    }));

  useEffect(() => {
    if (licenses) setFilteredOptions(formatLicenseOptions(licenses));
  }, [licenses]);

  const handleSearch = useCallback(
    (value: string) => {
      if (!licenses) return;
      const lowerCaseValue = value.toLowerCase();
      const filtered = formatLicenseOptions(licenses).filter(
        ({ label, value: val }) =>
          `${label} ${val}`.toLowerCase().includes(lowerCaseValue),
      );

      const licenseExists = filtered.some(
        (opt) => opt.value.toLowerCase() === lowerCaseValue,
      );

      if (value && !licenseExists) {
        filtered.unshift({
          label: value,
          value,
          type: NEW_LICENSE_TYPE,
        });
      }

      setFilteredOptions(filtered);
    },
    [licenses],
  );

  const handleDropdownVisibleChange = useCallback(
    (open: boolean) =>
      open && licenses && setFilteredOptions(formatLicenseOptions(licenses)),
    [licenses],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Backspace' && !selectedValue) handleSelectChange('');
    },
    [handleSelectChange, selectedValue],
  );

  return (
    <ParamFormItem
      param={param}
      disabled={disabled}
      setDisabled={setDisabled}
      visibleParams={visibleParams}
    >
      <Select
        value={selectedValue}
        disabled={disabled}
        allowClear
        placeholder="Select or create a license"
        onChange={handleSelectChange}
        onSearch={handleSearch}
        onKeyDown={handleKeyDown}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        showSearch
        filterOption={false}
        notFoundContent={
          isLoading ? (
            <Spin size="small" />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No licenses found"
            />
          )
        }
        style={{ width: '100%' }}
        options={filteredOptions.map(({ label, value, type }) => ({
          label: (
            <div>
              <span>{label}</span>
              {type === NEW_LICENSE_TYPE && (
                <Tag color="green" style={{ marginLeft: 4 }}>
                  New
                </Tag>
              )}
            </div>
          ),
          value,
        }))}
      />
    </ParamFormItem>
  );
};

export default RemoteProjectLicenseSelection;
